import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Loader2,
  RefreshCw,
  Pencil,
  Pause,
  Play,
  SkipBack,
  SkipForward,
  ShieldCheck,
  AlertCircle,
  Sparkles,
  ChevronDown,
  Download,
  Link2,
  RotateCcw,
  MoreVertical,
  Trash2,
} from "lucide-react";
import AppShell from "../components/Layout/AppShell";
import api from "../lib/api";
import { track } from "../lib/analytics";
import { toast } from "sonner";
import { useAuth } from "../lib/auth-context";
import { ScorecardsSection } from "../components/Scorecards/Scorecards";
import ComplianceDisclaimer from "../components/ComplianceDisclaimer";
import CandidatePicker from "../components/CandidatePicker";
import ChatPill from "../components/ChatPill";
import { InterviewersPanel } from "../components/Interviewers/InterviewersPanel";
import EditableTitle from "../components/EditableTitle";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "../components/ui/alert-dialog";

const STROKE = 1.5;

const ROLE_LABELS = {
  interviewer: "Interviewer",
  candidate: "Candidate",
  panelist: "Panelist",
  unknown: "?",
};
const ROLE_BADGE_CLASS = {
  interviewer: "bg-emerald-50 text-emerald-900 border-emerald-700/30",
  candidate: "bg-indigo-50 text-indigo-900 border-indigo-700/30",
  panelist: "bg-amber-50 text-amber-900 border-amber-700/30",
  unknown: "bg-ink-50 text-ink-500 border-ink-200",
};

function effectiveRole(speaker) {
  if (!speaker) return "unknown";
  return speaker.role || speaker.suggested_role || "unknown";
}

function transcriptEmptyMessage(pollJob, transcript) {
  if (pollJob && pollJob.status === "failed") {
    return "Transcript unavailable — the transcription job failed. Click Retry above to try again.";
  }
  if (pollJob && pollJob.status !== "completed") {
    return "Transcript will appear here when transcription completes.";
  }
  if (transcript) return "No spoken content was detected in this recording.";
  return "No transcript yet.";
}

function summaryEmptyMessage(pollJob, transcript) {
  if (pollJob && pollJob.status === "failed") return "Summary unavailable — transcription failed.";
  if (transcript && transcript.segments?.length === 0) {
    return "Awaiting transcript — summary will be generated when speech is detected.";
  }
  return "Summary will appear here when ready.";
}

function fmtTime(s) {
  if (!s || !isFinite(s)) return "00:00";
  const m = Math.floor(s / 60).toString().padStart(2, "0");
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}

export default function InterviewDetail() {
  const { interviewId } = useParams();
  const navigate = useNavigate();
  const { user, member } = useAuth();
  const [interview, setInterview] = useState(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [transcript, setTranscript] = useState(null);
  const [transcriptFetchError, setTranscriptFetchError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryFetchError, setSummaryFetchError] = useState(null);
  const [recording, setRecording] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pollJob, setPollJob] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [reTranscribeOpen, setReTranscribeOpen] = useState(false);
  const [startingTranscription, setStartingTranscription] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteCandidateName, setDeleteCandidateName] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const pollRef = useRef(null);
  // Imperative seek handle populated by AudioPlayer — lets the "Jump to
  // consent exchange" button drop the playhead on consent_spoken_at_seconds.
  const audioSeekApiRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: i } = await api.get(`/interviews/${interviewId}`);
      setInterview(i);
      if (i.recording_id) {
        try {
          const { data: r } = await api.get(`/recordings/${i.recording_id}`);
          setRecording(r);
        } catch {
          setRecording(null);
        }
      }
      // Transcript + summary fetches are wrapped in their own try/catch so a
      // backend 500 (e.g. May 30 SpeakerRole literal-validation bug) renders
      // a distinct "failed to load" state instead of the no-data empty state.
      // For a permanent-record product, an un-renderable transcript must
      // NEVER be visually indistinguishable from a deleted one.
      if (i.transcript_id) {
        try {
          const { data: t } = await api.get(`/transcripts/${i.transcript_id}`);
          setTranscript(t);
          setTranscriptFetchError(null);
        } catch (err) {
          setTranscript(null);
          setTranscriptFetchError(
            err?.response?.data?.detail ||
            err?.message ||
            "Internal Server Error"
          );
        }
      } else {
        setTranscript(null);
        setTranscriptFetchError(null);
      }
      if (i.summary_id) {
        try {
          const { data: s } = await api.get(`/summaries/${i.summary_id}`);
          setSummary(s);
          setSummaryFetchError(null);
        } catch (err) {
          setSummary(null);
          setSummaryFetchError(
            err?.response?.data?.detail ||
            err?.message ||
            "Internal Server Error"
          );
        }
      } else {
        setSummary(null);
        setSummaryFetchError(null);
      }
      // If a job is in-flight, poll it
      if (i.job_id && !i.transcript_id) {
        const { data: j } = await api.get(`/transcribe/${i.job_id}`);
        setPollJob(j);
        if (j.status !== "completed" && j.status !== "failed") {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = setInterval(async () => {
            try {
              const { data: latest } = await api.get(`/transcribe/${i.job_id}`);
              setPollJob(latest);
              if (latest.status === "completed" || latest.status === "failed") {
                clearInterval(pollRef.current);
                pollRef.current = null;
                if (latest.status === "completed") track("transcription_completed", {});
                load();
              }
            } catch (err) {
              console.warn("polling job status failed:", err?.message || err);
            }
          }, 2000);
        }
      } else {
        setPollJob(null);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load interview");
    } finally {
      setLoading(false);
    }
  }, [interviewId]);

  useEffect(() => {
    load();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [load]);

  const retryTranscription = async () => {
    if (!interview?.job_id && !interview?.recording_id) return;
    if (retrying) return;          // belt-and-suspenders against double-click
    setRetrying(true);
    try {
      // Phase 2a — long-form recordings have a dedicated retry endpoint that
      // skips succeeded slices (no double-charge). Short recordings fall
      // through to the existing whole-job retry.
      if (transcript?.slice_outcomes?.length > 0 && interview.recording_id) {
        const { data } = await api.post(`/recordings/${interview.recording_id}/retry-long-form`);
        const willRetry = data?.slices_will_retry?.length || 0;
        const permFail = data?.slices_permanently_failed?.length || 0;
        if (permFail > 0) {
          toast.warning(`Retrying ${willRetry} failed slice${willRetry === 1 ? "" : "s"} · ${permFail} permanently failed (attempted 3×). Re-record to recover those.`);
        } else {
          toast.success(`Retrying ${willRetry} failed slice${willRetry === 1 ? "" : "s"} (succeeded slices skipped — not re-charged)`);
        }
      } else if (interview.job_id) {
        await api.post(`/transcribe/${interview.job_id}/retry`);
        toast.success("Retrying transcription — this may take a minute");
      }
      // Phase 4.5 — poll the job status so the user sees progress AND so we
      // refresh the transcript (and the fallback banner) automatically when
      // the retry finishes. Without this poll, the user has to manually
      // refresh the page and may click Retry multiple times in confusion
      // (each click would 400 with "Job is still running" until the BG task
      // finishes). Poll every 2.5s with a hard cap of 10 min.
      const startedAt = Date.now();
      const pollHandle = setInterval(async () => {
        try {
          if (Date.now() - startedAt > 10 * 60 * 1000) {
            clearInterval(pollHandle);
            setRetrying(false);
            return;
          }
          if (!interview?.job_id) {
            clearInterval(pollHandle);
            setRetrying(false);
            return;
          }
          const { data: j } = await api.get(`/transcribe/${interview.job_id}`);
          if (j.status === "completed" || j.status === "failed") {
            clearInterval(pollHandle);
            if (j.status === "completed") track("transcription_completed", {});
            await load();           // refresh transcript + banner
            setRetrying(false);
          }
        } catch (e) {
          clearInterval(pollHandle);
          setRetrying(false);
        }
      }, 2500);
      // First refresh attempt right away so the queued status shows up
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Retry failed");
      setRetrying(false);
    }
  };

  const regenSummary = async () => {
    if (!summary) return;
    try {
      await api.post(`/summaries/${summary.summary_id}/regenerate`);
      toast.success("Summary is regenerating in the background");
      setTimeout(load, 4000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Regeneration failed");
    }
  };

  // CR-2 — rescue path for an interview whose recording finalized but whose
  // transcription job was never created (kickoff failed post-finalize). On
  // success the backend sets interview.job_id, so load() picks up the job and
  // the existing processing-banner poll takes over.
  const startTranscription = async () => {
    if (startingTranscription || !interview?.recording_id) return;
    setStartingTranscription(true);
    try {
      await api.post("/transcribe", { recording_id: interview.recording_id });
      toast.success("Transcription started");
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error((typeof detail === "string" && detail) || "Couldn't start transcription");
    } finally {
      setStartingTranscription(false);
    }
  };

  // Phase 4b — Export PDF. Streams bytes from POST /api/interviews/:id/export-pdf,
  // pulls the server-set filename from Content-Disposition (RFC 5987 dual form),
  // and triggers a browser download via an object-URL anchor. Single, atomic
  // user action — no polling, no job queue. WeasyPrint render is fast (<3s for
  // typical 30-min interview).
  const exportPdf = async () => {
    if (!interview?.interview_id || exporting) return;
    setExporting(true);
    try {
      const resp = await api.post(
        `/interviews/${interview.interview_id}/export-pdf`,
        null,
        { responseType: "blob" },
      );
      const cd = resp.headers?.["content-disposition"] || "";
      // Prefer RFC 5987 utf-8 filename when present, else fall back to ascii
      // filename, else a sensible default.
      let filename = "Hiretape_Interview.pdf";
      const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(cd);
      const asciiMatch = /filename="([^"]+)"/.exec(cd);
      if (utf8Match) {
        try { filename = decodeURIComponent(utf8Match[1]); } catch { /* keep default */ }
      } else if (asciiMatch) {
        filename = asciiMatch[1];
      }
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("PDF downloaded");
    } catch (err) {
      // Blob error responses can't be read directly — coerce to text first.
      let detail = "Export failed";
      try {
        const text = await err?.response?.data?.text?.();
        if (text) {
          const j = JSON.parse(text);
          detail = j.detail || detail;
        }
      } catch { /* keep default */ }
      toast.error(detail);
    } finally {
      setExporting(false);
    }
  };

  // Feature 1 — Delete interview (admin-only). Confirmation requires typing
  // the candidate's name (fetched lazily when the dialog opens); orphan
  // interviews (no candidate) fall back to typing DELETE.
  const openDeleteDialog = async () => {
    setDeleteConfirmText("");
    let name = null;
    if (interview?.candidate_id) {
      try {
        const { data } = await api.get(`/candidates/${interview.candidate_id}`);
        name = (data?.name || "").trim() || null;
      } catch { /* fall back to DELETE keyword */ }
    }
    setDeleteCandidateName(name);
    setDeleteOpen(true);
  };
  const deleteConfirmWord = deleteCandidateName || "DELETE";
  const deleteInterview = async () => {
    if (deleting || deleteConfirmText.trim() !== deleteConfirmWord) return;
    setDeleting(true);
    try {
      await api.delete(`/interviews/${interview.interview_id}`);
      toast.success("Interview permanently deleted");
      navigate("/interviews");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error((typeof detail === "string" && detail) || "Delete failed");
      setDeleting(false);
    }
  };

  if (loading && !interview) {
    return (
      <AppShell>
        <div className="flex h-64 items-center justify-center text-ink-400">
          <Loader2 className="h-5 w-5 animate-spin" strokeWidth={STROKE} />
        </div>
      </AppShell>
    );
  }

  // Re-transcribe gating: recruiter+ only (viewers get no button; the backend
  // /transcribe/{job_id}/retry also 403s them). Available whenever a transcript
  // already exists — this is the "re-run to fix a legacy non-diarizing
  // transcript" action, distinct from the failure-only Retry banner.
  const canReTranscribe = ["recruiter", "admin"].includes(member?.role);
  const isAdmin = member?.role === "admin";
  const isFreeSeat = member?.role === "hiring_manager" || member?.role === "viewer";
  const jobBusy =
    !!pollJob && pollJob.status !== "completed" && pollJob.status !== "failed";
  const showReTranscribe =
    canReTranscribe && !!interview?.job_id && !!interview?.transcript_id;
  const openReTranscribe = () => setReTranscribeOpen(true);

  return (
    <AppShell>
      <div data-testid="interview-detail-page" className="space-y-8">
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <button
              onClick={() => navigate("/interviews")}
              className="text-[13px] text-ink-500 hover:text-ink-900"
              data-testid="back-to-interviews"
            >
              ← All interviews
            </button>
            <h1 className="font-display mt-2 text-3xl font-medium tracking-tight text-ink-900">
              <EditableTitle
                value={interview?.title || ""}
                canEdit={canReTranscribe}
                testid="interview-title"
                placeholder="Untitled interview"
                onSave={async (next) => {
                  const { data: updated } = await api.patch(`/interviews/${interview.interview_id}`, { title: next });
                  setInterview(updated);
                  toast.success("Interview renamed");
                }}
              />
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-[12px] text-ink-500">
              <span className="inline-flex items-center gap-1 rounded-md border border-emerald-800/15 bg-emerald-50 px-2 py-0.5 text-emerald-800">
                <ShieldCheck className="h-3 w-3" strokeWidth={STROKE} />
                Consent captured
              </span>
              <span>Status: <span className="font-medium text-ink-800">{interview?.status}</span></span>
              <span>Round {interview?.round_number}</span>
              <span className="capitalize">{interview?.type}</span>
              <span className="capitalize">{interview?.location_type?.replace("_", " ")}</span>
            </div>
          </div>
          {/* Primary action: Export PDF. Solid emerald, leading icon, taller than
              utility buttons (Retry/Regenerate) so it reads as the canonical
              "save the HR record" action. Disabled until at least a transcript
              or scorecard exists — exporting an empty interview is a misclick. */}
          <div className="flex shrink-0 items-center gap-2">
            {showReTranscribe && (
              <button
                onClick={openReTranscribe}
                disabled={retrying || jobBusy}
                className="inline-flex items-center gap-2 rounded-md border border-ink-200 bg-white px-4 py-2.5 text-[13px] font-medium text-ink-700 shadow-sm transition-all hover:bg-ink-50 disabled:cursor-not-allowed disabled:opacity-60"
                data-testid="re-transcribe-button"
                title="Re-run transcription on the original audio. Creates a new version; the current transcript is preserved."
              >
                {retrying ? (
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} />
                ) : (
                  <RotateCcw className="h-4 w-4" strokeWidth={STROKE} />
                )}
                {retrying ? "Re-transcribing…" : "Re-transcribe"}
              </button>
            )}
            {!isFreeSeat && (
              <button
                onClick={exportPdf}
                disabled={exporting || !interview}
                className="inline-flex items-center gap-2 rounded-md bg-emerald-800 px-4 py-2.5 text-[13px] font-medium text-white shadow-sm transition-all hover:bg-emerald-900 hover:shadow disabled:cursor-not-allowed disabled:opacity-60"
                data-testid="export-pdf-button"
                title="Download a permanent HR-file PDF of this interview record"
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} />
                ) : (
                  <Download className="h-4 w-4" strokeWidth={STROKE} />
                )}
                {exporting ? "Generating…" : "Export PDF"}
              </button>
            )}
            {isAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="inline-flex items-center justify-center rounded-md border border-ink-200 bg-white p-2.5 text-ink-600 shadow-sm transition-all hover:bg-ink-50"
                    data-testid="interview-overflow-menu"
                    aria-label="More actions"
                  >
                    <MoreVertical className="h-4 w-4" strokeWidth={STROKE} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={openDeleteDialog}
                    className="text-red-600 focus:text-red-700"
                    data-testid="delete-interview-menu-item"
                  >
                    <Trash2 className="mr-2 h-4 w-4" strokeWidth={STROKE} />
                    Delete interview
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </header>

        <InterviewersPanel
          interview={interview}
          canEdit={canReTranscribe}
          onUpdated={(updated) => setInterview((prev) => ({ ...prev, ...updated }))}
        />

        {/* Phase 4d — orphan rescue banner: this interview isn't linked
            to a candidate yet, so it's invisible on candidate profiles +
            AI Chat can't search it. */}
        {interview && !interview.candidate_id && (
          <div className="hp-card border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="link-candidate-banner">
            <Link2 className="h-4 w-4 text-amber-800 mt-0.5" strokeWidth={STROKE} />
            <div className="flex-1">
              <div className="text-[14px] font-medium text-amber-900">This interview isn&rsquo;t linked to a candidate yet</div>
              <div className="mt-1 text-[13px] text-amber-800">
                Link it so it shows up on the candidate profile and the audit-record chat can search across rounds.
              </div>
            </div>
            <button
              onClick={() => setLinkOpen(true)}
              className="rounded-md border border-amber-800 bg-white px-3 py-1.5 text-[13px] font-medium text-amber-900 hover:bg-amber-100"
              data-testid="link-candidate-open"
            >
              Link to candidate
            </button>
          </div>
        )}


        {/* CR-2 — rescue affordance: recording saved but transcription never
            started (job kickoff failed post-finalize, or legacy stuck state).
            Recruiter+ only, mirroring the /transcribe endpoint's role gate. */}
        {canReTranscribe && interview?.recording_id && !interview?.transcript_id &&
          !interview?.job_id && recording?.status === "ready" && (
          <div className="hp-card border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="start-transcription-banner">
            <AlertCircle className="h-4 w-4 text-amber-800 mt-0.5" strokeWidth={STROKE} />
            <div className="flex-1">
              <div className="text-[14px] font-medium text-amber-900">Recording saved, but no transcript yet</div>
              <div className="mt-1 text-[13px] text-amber-800">
                The audio for this interview is safely stored, but transcription was never started.
              </div>
            </div>
            <button
              onClick={startTranscription}
              disabled={startingTranscription}
              className="rounded-md border border-amber-800 bg-white px-3 py-1.5 text-[13px] font-medium text-amber-900 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
              data-testid="start-transcription-button"
            >
              {startingTranscription ? "Starting…" : "Start transcription"}
            </button>
          </div>
        )}

        {/* Processing banner */}
        {pollJob && pollJob.status !== "completed" && pollJob.status !== "failed" && (
          <div className="hp-card flex items-center gap-3 p-4" data-testid="processing-banner">
            <Loader2 className="h-4 w-4 animate-spin text-emerald-800" strokeWidth={STROKE} />
            <div className="flex-1">
              <div className="text-[14px] font-medium text-ink-900">{pollJob.stage || "Processing"} — {pollJob.progress || 0}%</div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-ink-100">
                <div className="h-full bg-emerald-800 transition-all" style={{ width: `${pollJob.progress || 0}%` }} />
              </div>
            </div>
          </div>
        )}
        {pollJob?.status === "failed" && (
          <div className="hp-card border-red-200 bg-red-50 p-4 flex items-start gap-3" data-testid="failure-banner">
            <AlertCircle className="h-4 w-4 text-red-700 mt-0.5" strokeWidth={STROKE} />
            <div className="flex-1">
              <div className="text-[14px] font-medium text-red-900">Transcription failed</div>
              <div className="mt-1 text-[13px] text-red-700">{pollJob.error}</div>
            </div>
            <button onClick={retryTranscription} className="hp-btn-outline" data-testid="retry-transcription">
              <RefreshCw className="h-4 w-4" strokeWidth={STROKE} /> Retry
            </button>
          </div>
        )}

        {/* Audio + transcript layout */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-7 space-y-6">
            {interview?.recording_id && (
              <AudioPlayer
                recordingId={interview.recording_id}
                fallbackDuration={recording?.duration_seconds}
                seekApiRef={audioSeekApiRef}
              />
            )}
            {interview?.consent_spoken_at_seconds != null && (
              <div
                className="hp-card flex items-center justify-between gap-3 border-emerald-200 bg-emerald-50/50 px-4 py-2.5"
                data-testid="consent-jump-banner"
              >
                <div className="flex items-center gap-2 text-[13px] text-emerald-900">
                  <ShieldCheck className="h-4 w-4" strokeWidth={STROKE} />
                  <span>
                    Consent spoken at{" "}
                    <span
                      className="font-mono tabular-nums font-medium"
                      data-testid="consent-jump-timestamp"
                    >
                      {fmtTime(interview.consent_spoken_at_seconds)}
                    </span>
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (interview.recording_id && audioSeekApiRef.current?.seekTo) {
                      audioSeekApiRef.current.seekTo(interview.consent_spoken_at_seconds);
                    }
                  }}
                  disabled={!interview.recording_id}
                  className="hp-btn-outline"
                  data-testid="jump-to-consent-button"
                >
                  Jump to consent exchange
                </button>
              </div>
            )}
            {transcript?.slice_outcomes?.length > 0 && (
              <SliceCoverageBar
                outcomes={transcript.slice_outcomes}
                slicesTotal={transcript.slices_total || transcript.slice_outcomes.length}
                slicesFailed={transcript.slices_failed || 0}
                recordingDuration={recording?.duration_seconds || 0}
              />
            )}
            {transcript?.asr_fallback && (
              <AsrFallbackBanner
                fallback={transcript.asr_fallback}
                onRetry={retryTranscription}
                retrying={retrying}
              />
            )}
            {transcript && !transcript.asr_fallback && transcript.diarization_performed === false && (
              <NoDiarizationNote
                canReTranscribe={canReTranscribe}
                onReTranscribe={openReTranscribe}
                retrying={retrying}
              />
            )}
            {transcript && transcript.segments?.length > 0 ? (
              <TranscriptView
                transcript={transcript}
                onUpdate={(t) => setTranscript(t)}
              />
            ) : transcriptFetchError && interview?.transcript_id ? (
              // Distinct "fetch errored" state — must NOT look like the
              // "no transcript yet" empty state. A permanent-record product
              // cannot let an un-renderable transcript appear deleted.
              <div className="hp-card p-8 border-red-200 bg-red-50/40" data-testid="transcript-fetch-error">
                <div className="text-[14px] font-semibold text-red-900">Transcript failed to load</div>
                <div className="mt-1 text-[13px] text-red-700">
                  The transcript exists but couldn&rsquo;t be rendered. Server returned: <span className="font-mono">{transcriptFetchError}</span>
                </div>
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={load}
                    data-testid="transcript-retry-load"
                    className="text-[13px] font-medium text-emerald-800 hover:text-emerald-900 underline underline-offset-2"
                  >
                    Retry loading transcript
                  </button>
                </div>
              </div>
            ) : (
              <div className="hp-card p-8 text-center text-ink-500 text-[14px]" data-testid="transcript-unavailable">
                {transcriptEmptyMessage(pollJob, transcript)}
              </div>
            )}
          </div>
          <div className="lg:col-span-5 space-y-6">
            {summary && transcript && transcript.segments?.length > 0 ? (
              <SummaryView summary={summary} onRegenerate={regenSummary} canRegenerate={!isFreeSeat} />
            ) : summaryFetchError && interview?.summary_id ? (
              <div className="hp-card p-8 border-red-200 bg-red-50/40" data-testid="summary-fetch-error">
                <div className="text-[14px] font-semibold text-red-900">Summary failed to load</div>
                <div className="mt-1 text-[13px] text-red-700">
                  The summary exists but couldn&rsquo;t be rendered. Server returned: <span className="font-mono">{summaryFetchError}</span>
                </div>
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={load}
                    data-testid="summary-retry-load"
                    className="text-[13px] font-medium text-emerald-800 hover:text-emerald-900 underline underline-offset-2"
                  >
                    Retry loading summary
                  </button>
                </div>
              </div>
            ) : (
              <div className="hp-card p-8 text-center text-ink-500 text-[14px]" data-testid="summary-unavailable">
                {summaryEmptyMessage(pollJob, transcript)}
              </div>
            )}
            {/* Phase 4a — Scorecards section. Read-only for viewers; HM+ can create. */}
            <ScorecardsSection
              interviewId={interviewId}
              currentUser={user}
              currentMemberRole={member?.role}
            />
          </div>
        </div>
      </div>
      <LinkCandidateDialog
        open={linkOpen}
        onOpenChange={setLinkOpen}
        interviewId={interviewId}
        onLinked={(updated) => {
          setInterview(updated);
          setLinkOpen(false);
          toast.success(`Linked to candidate as Round ${updated.round_number}`);
        }}
      />
      {/* Re-transcribe confirmation. Controlled (no trigger) so both the header
          button and the no-diarization note open the SAME dialog. On confirm it
          runs the existing atomic-swap retry (retryTranscription) — the old
          transcript is superseded but preserved as a version. */}
      <AlertDialog open={reTranscribeOpen} onOpenChange={setReTranscribeOpen}>
        <AlertDialogContent data-testid="re-transcribe-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl font-medium">
              Re-transcribe this interview?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] text-ink-600">
              This re-runs transcription on the original audio using this
              workspace&rsquo;s preferred engine (for example, to add speaker
              separation).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="text-[13px] text-ink-700" data-testid="re-transcribe-warning">
            <ul className="list-disc space-y-2 pl-5">
              <li>
                <strong>Your current transcript is preserved.</strong> It becomes
                a prior version &mdash; nothing is deleted, and it stays queryable
                in this interview&rsquo;s transcript history.
              </li>
              <li>
                <strong>Your manual speaker-label edits won&rsquo;t carry over.</strong>{" "}
                Any speaker names or segment reassignments you made by hand will{" "}
                <strong>not</strong> be applied to the new transcript &mdash;
                you&rsquo;ll need to redo them.
              </li>
              <li>
                The new transcript replaces the current one only if it passes the
                quality check. A failed or lower-quality result leaves your
                existing transcript untouched.
              </li>
            </ul>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="re-transcribe-cancel">
              Keep current transcript
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="re-transcribe-confirm"
              className="bg-emerald-800 hover:bg-emerald-900"
              onClick={() => {
                setReTranscribeOpen(false);
                retryTranscription();
              }}
            >
              Re-transcribe
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Feature 1 — Delete interview confirmation (admin-only entry point).
          Requires typing the candidate's name (or DELETE for orphans). */}
      <AlertDialog open={deleteOpen} onOpenChange={(o) => { if (!deleting) setDeleteOpen(o); }}>
        <AlertDialogContent data-testid="delete-interview-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl font-medium">
              Delete this interview permanently?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] text-ink-600">
              This permanently deletes the recording, transcript, and all derived
              records. This cannot be undone. Files already synced to Google Drive
              are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 text-[13px] text-ink-700">
            <p data-testid="delete-interview-confirm-label">
              Type <strong>{deleteConfirmWord}</strong>
              {deleteCandidateName ? " (the candidate's name)" : ""} to confirm:
            </p>
            <input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className="w-full rounded-md border border-ink-200 px-3 py-2 text-[13px] focus:border-red-400 focus:outline-none"
              placeholder={deleteConfirmWord}
              data-testid="delete-interview-confirm-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-interview-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); deleteInterview(); }}
              disabled={deleting || deleteConfirmText.trim() !== deleteConfirmWord}
              className="bg-red-600 hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="delete-interview-confirm-button"
            >
              {deleting ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Persistent bottom-right chat pill.
          - When linked: opens the SAME candidate-scoped chat, with the
            first message pre-drafted as "Tell me about Round N" so the
            recruiter has context but can edit before sending.
          - When orphan: amber variant; clicking opens the link dialog
            instead of an empty chat (chat needs a candidate context). */}
      {isFreeSeat ? null : interview && interview.candidate_id ? (
        <ChatPill
          candidateId={interview.candidate_id}
          initialDraft={`Tell me about Round ${interview.round_number || "this"} — what was discussed?`}
        />
      ) : interview ? (
        <ChatPill orphanMode onOrphanRequestLink={() => setLinkOpen(true)} />
      ) : null}
    </AppShell>
  );
}

function LinkCandidateDialog({ open, onOpenChange, interviewId, onLinked }) {
  const [submitting, setSubmitting] = useState(false);
  async function handlePick({ candidate_id }) {
    setSubmitting(true);
    try {
      const { data } = await api.post(
        `/interviews/${interviewId}/assign-candidate`,
        { candidate_id },
      );
      onLinked(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to link");
    } finally {
      setSubmitting(false);
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="link-candidate-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-medium">Link to a candidate</DialogTitle>
          <DialogDescription className="text-[13px] text-ink-500">
            Pick the candidate this interview is for. The round number will auto-compute based on their existing rounds.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          {submitting && (
            <div className="mb-2 flex items-center gap-2 text-[12px] text-ink-500">
              <Loader2 className="h-3 w-3 animate-spin" strokeWidth={STROKE} /> Linking…
            </div>
          )}
          <CandidatePicker onSelect={handlePick} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Slice Coverage Bar (Phase 2a) ----------

function fmtHms(seconds) {
  if (!seconds || !isFinite(seconds)) return "00:00";
  const s = Math.max(Math.floor(seconds), 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60).toString().padStart(2, "0");
  const sec = (s % 60).toString().padStart(2, "0");
  return h > 0 ? `${h}:${m}:${sec}` : `${m}:${sec}`;
}

/**
 * Renders one horizontal bar with one tile per slice, colored by status.
 * Hover-tooltip shows slice timestamp + status + error. Only present on
 * long-form transcripts (recordings >25min that went through chunk-stitching).
 *
 * Why this exists: when one of N slices fails, the transcript shows
 * "[transcription failed — HH:MM:SS slice N/M]" inline. Without this bar
 * users mistake partial-failure for total product failure. Visualizing
 * coverage is core to the "legal record" trust positioning.
 */

/**
 * Phase 4.5 — visible-fallback banner. Renders above the transcript whenever
 * `transcripts.asr_fallback` is set, which the backend writes when a
 * workspace had AssemblyAI selected but the actual run used a different
 * provider (key missing / API timeout / runtime error). Surfaces the missed
 * diarization so the recruiter knows speaker labels are stale — never
 * silently downgrade the experience.
 */
function AsrFallbackBanner({ fallback, onRetry, retrying }) {
  // Backend writes provider-specific reasons (e.g. "AssemblyAI: 503"); we
  // scrub the vendor name out of customer-visible text and present a
  // region-agnostic fallback message instead.
  const rawReason = fallback?.reason || "Preferred engine was unavailable";
  // Oct 3, 2026 — reason-specific copy for the one case we have measured: the
  // India engine queued a 28-second file for 29 minutes. "Slow" is the honest
  // word; the engine did not fail, it did not answer in time.
  const indiaEngineSlow =
    fallback?.preferred_provider === "sarvam" && /timed out|timeout/i.test(String(rawReason));
  const sanitizedReason = String(rawReason)
    .replace(/assemblyai/gi, "primary engine")
    .replace(/sarvam/gi, "primary engine")
    .replace(/whisper/gi, "backup engine")
    .replace(/gemini/gi, "")
    .trim() || "Preferred engine was unavailable";
  const headline = indiaEngineSlow
    ? "Transcribed on the backup engine because the India engine was slow"
    : "Transcribed via backup engine — speaker labels unavailable";
  const detail = indiaEngineSlow
    ? "Speaker labels in this transcript are not separated by voice. Retry to attempt the India engine again — it is given a longer window on a manual retry."
    : `${sanitizedReason}. Speaker labels in this transcript are not separated by voice. Retry to attempt the preferred engine again.`;
  return (
    <div
      className="hp-card border-amber-300 bg-amber-50/70 p-4"
      data-testid="asr-fallback-banner"
      data-fallback-kind={indiaEngineSlow ? "india-engine-slow" : "generic"}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-200 text-amber-900">
          <AlertCircle className="h-3 w-3" strokeWidth={2} />
        </div>
        <div className="flex-1">
          <div className="text-[13px] font-medium text-amber-950" data-testid="asr-fallback-headline">
            {headline}
          </div>
          <div className="mt-1 text-[12px] text-amber-900/80" data-testid="asr-fallback-reason">
            {detail}
          </div>
        </div>
        <button
          onClick={onRetry}
          disabled={retrying}
          className="inline-flex shrink-0 items-center gap-2 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-[12px] font-medium text-amber-900 transition-colors hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
          data-testid="asr-fallback-retry-btn"
        >
          {retrying ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />
              Retrying…
            </>
          ) : (
            "Retry with preferred engine"
          )}
        </button>
      </div>
    </div>
  );
}

function NoDiarizationNote({ canReTranscribe, onReTranscribe, retrying }) {
  // Shown when a transcript was produced by a non-diarizing engine
  // (deliberately-selected Whisper/Sarvam) — NOT a fallback. For an audit
  // product a single-speaker transcript must never look like a successful
  // diarization. Vendor-neutral copy, consistent with the fallback banner.
  return (
    <div
      className="hp-card border-amber-300 bg-amber-50/70 p-4"
      data-testid="no-diarization-note"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-200 text-amber-900">
          <AlertCircle className="h-3 w-3" strokeWidth={2} />
        </div>
        <div className="flex-1">
          <div className="text-[13px] font-medium text-amber-950">
            Speaker separation not performed for this transcript
          </div>
          <div className="mt-1 text-[12px] text-amber-900/80" data-testid="no-diarization-reason">
            This workspace&rsquo;s transcription engine does not distinguish voices, so all speech is shown under a single speaker. To separate Interviewer and Candidate, switch the transcription region to &ldquo;US &amp; Canada&rdquo; in Settings, then re-transcribe this interview.
          </div>
        </div>
        {canReTranscribe && (
          <button
            onClick={onReTranscribe}
            disabled={retrying}
            className="inline-flex shrink-0 items-center gap-2 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-[12px] font-medium text-amber-900 transition-colors hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="no-diarization-retranscribe-btn"
          >
            {retrying ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />
                Re-transcribing…
              </>
            ) : (
              "Re-transcribe"
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function SliceCoverageBar({ outcomes, slicesTotal, slicesFailed, recordingDuration }) {
  const succeeded = slicesTotal - slicesFailed;
  const allOk = slicesFailed === 0;
  return (
    <div className="hp-card p-4" data-testid="slice-coverage-bar">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[13px]">
          <span className={`inline-flex h-2 w-2 rounded-full ${allOk ? "bg-emerald-700" : "bg-amber-500"}`} />
          <span className="font-medium text-ink-900">
            {succeeded} of {slicesTotal} slices transcribed
          </span>
          {!allOk && (
            <span className="text-ink-500">
              · {slicesFailed} gap{slicesFailed > 1 ? "s" : ""} in transcript
            </span>
          )}
        </div>
        {recordingDuration > 0 && (
          <span className="text-[12px] text-ink-500" data-testid="slice-coverage-duration">
            Total: {fmtHms(recordingDuration)}
          </span>
        )}
      </div>
      <div
        className="flex h-3 overflow-hidden rounded-md border border-ink-200 bg-ink-50"
        role="img"
        aria-label={`Slice coverage: ${succeeded} of ${slicesTotal} slices transcribed`}
      >
        {outcomes.map((o, i) => {
          const ok = o.status === "succeeded";
          const tipParts = [
            `Slice ${i + 1}/${outcomes.length}`,
            ok ? "Transcribed" : `Failed (attempt ${o.attempt_count || 0}/3)`,
          ];
          if (o.provider_used && ok) tipParts.push(`via ${o.provider_used}`);
          if (o.error && !ok) tipParts.push(o.error);
          return (
            <span
              key={i}
              className={`block border-r border-white/40 last:border-r-0 ${
                ok ? "bg-emerald-700" : "bg-red-500"
              }`}
              style={{ flex: 1 }}
              title={tipParts.join(" • ")}
              data-testid={`slice-tile-${i}`}
              data-slice-status={o.status}
            />
          );
        })}
      </div>
      {!allOk && (
        <p className="mt-2 text-[12px] text-ink-500" data-testid="slice-coverage-hint">
          Hover a red segment for the timestamp. Use <span className="font-medium">Retry transcription</span> above to re-attempt failed slices — succeeded slices are NOT re-charged.
        </p>
      )}
    </div>
  );
}

// ---------- Audio player ----------

function AudioPlayer({ recordingId, fallbackDuration, seekApiRef }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [audioUrl, setAudioUrl] = useState("");
  const [loadError, setLoadError] = useState("");

  // Effective duration: prefer browser metadata if it's a valid finite number > 0;
  // otherwise fall back to the backend-stored duration_seconds. WebM chunks
  // stitched together can have missing or zero duration metadata.
  const effectiveDur = dur > 0 && isFinite(dur) ? dur : (fallbackDuration || 0);

  // Phase 2a fix: the <audio> element can't inject our localStorage JWT into
  // its native fetch for `src`, so a plain `src=...` URL hits the protected
  // endpoint without auth → 401 → silent playback failure. Fetch via axios
  // (which DOES inject the JWT via our request interceptor), turn the
  // response into a Blob, and feed the audio element a blob: URL it can
  // play without re-fetching.
  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    setLoadError("");
    (async () => {
      try {
        const resp = await api.get(`/recordings/${recordingId}/audio`, {
          responseType: "blob",
        });
        if (cancelled) return;
        const blob = resp.data;
        createdUrl = URL.createObjectURL(blob);
        setAudioUrl(createdUrl);
      } catch (err) {
        if (cancelled) return;
        const msg = err?.response?.status === 401
          ? "Sign-in expired — refresh the page."
          : err?.response?.status === 404
          ? "Audio file missing on server."
          : "Couldn't load audio.";
        setLoadError(msg);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [recordingId]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => setCur(el.currentTime);
    const onDur = () => {
      const d = el.duration;
      setDur(d && isFinite(d) ? d : 0);
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onDur);
    el.addEventListener("durationchange", onDur);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onDur);
      el.removeEventListener("durationchange", onDur);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
    };
  }, [audioUrl]);

  // Pause on navigation
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "hidden" && audioRef.current && !audioRef.current.paused) {
        audioRef.current.pause();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const seek = (delta) => {
    const el = audioRef.current;
    if (!el) return;
    const target = el.currentTime + delta;
    const cap = effectiveDur || 0;
    el.currentTime = Math.max(0, cap ? Math.min(cap, target) : target);
  };

  // Expose imperative seek-to-absolute to the parent so the "Jump to consent
  // exchange" affordance can drop the playhead exactly on
  // consent_spoken_at_seconds without threading state through props.
  useEffect(() => {
    if (!seekApiRef) return;
    seekApiRef.current = {
      seekTo: (seconds) => {
        const el = audioRef.current;
        if (!el || !Number.isFinite(seconds)) return;
        const cap = effectiveDur || 0;
        const target = Math.max(0, cap ? Math.min(cap, seconds) : seconds);
        el.currentTime = target;
        try { el.play(); } catch { /* auto-play may be blocked; harmless */ }
      },
    };
    return () => {
      if (seekApiRef.current) seekApiRef.current = null;
    };
  }, [seekApiRef, effectiveDur, audioUrl]);

  return (
    <div className="hp-card p-4" data-testid="audio-player">
      <audio ref={audioRef} src={audioUrl || undefined} preload="metadata" />
      <div className="flex items-center gap-3">
        <button
          onClick={() => (audioRef.current?.paused ? audioRef.current.play() : audioRef.current.pause())}
          className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-emerald-800 text-white transition hover:bg-emerald-900"
          data-testid="audio-play-toggle"
        >
          {playing ? <Pause className="h-4 w-4" strokeWidth={STROKE} /> : <Play className="h-4 w-4 fill-white" strokeWidth={STROKE} />}
        </button>
        <button onClick={() => seek(-10)} className="hp-btn-ghost" data-testid="audio-seek-back">
          <SkipBack className="h-4 w-4" strokeWidth={STROKE} /> 10s
        </button>
        <button onClick={() => seek(10)} className="hp-btn-ghost" data-testid="audio-seek-forward">
          10s <SkipForward className="h-4 w-4" strokeWidth={STROKE} />
        </button>
        <div className="ml-2 flex-1">
          <input
            type="range"
            min={0}
            max={effectiveDur || 0}
            value={cur}
            onChange={(e) => { audioRef.current.currentTime = Number(e.target.value); }}
            className="w-full accent-emerald-800"
            data-testid="audio-seek-bar"
          />
        </div>
        <div className="font-mono text-[12px] tabular-nums text-ink-600" data-testid="audio-time">
          {fmtTime(cur)} / {fmtTime(effectiveDur)}
        </div>
      </div>
      {loadError && (
        <div
          className="mt-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700"
          data-testid="audio-load-error"
        >
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" strokeWidth={STROKE} />
          {loadError}
        </div>
      )}
    </div>
  );
}

// ---------- Transcript view with editable speakers ----------

function TranscriptView({ transcript, onUpdate }) {
  const speakers = transcript.speakers || [];
  const speakerById = useMemo(() => {
    const m = {};
    speakers.forEach((s) => { m[s.speaker_id] = s; });
    return m;
  }, [speakers]);

  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [selectionStart, setSelectionStart] = useState(null);
  const [creatingSpeaker, setCreatingSpeaker] = useState(false);
  const [newSpeakerName, setNewSpeakerName] = useState("");
  const [pendingRanges, setPendingRanges] = useState([]); // for visual highlight

  const renameSpeaker = async (speakerId, name) => {
    try {
      const { data } = await api.patch(`/transcripts/${transcript.transcript_id}/speakers/${speakerId}`, { display_name: name });
      onUpdate(data);
      toast.success("Speaker renamed everywhere");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rename failed");
    } finally {
      setRenamingId(null);
    }
  };

  const reassign = async (assignments) => {
    if (!assignments.length) return;
    try {
      const { data } = await api.patch(`/transcripts/${transcript.transcript_id}/segments`, { assignments });
      onUpdate(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reassign failed");
    }
  };

  const addSpeaker = async () => {
    if (!newSpeakerName.trim()) return;
    try {
      const { data } = await api.post(`/transcripts/${transcript.transcript_id}/speakers`, { display_name: newSpeakerName.trim() });
      onUpdate(data);
      setNewSpeakerName("");
      setCreatingSpeaker(false);
      toast.success(`Added "${newSpeakerName.trim()}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Add speaker failed");
    }
  };

  // Right-click "rename all instances of Speaker X" — finds every segment with that speaker_id
  // and reassigns them in one bulk request. Or, when there's a new speaker name supplied, we
  // rename the speaker (which propagates everywhere via the lookup).
  const onSegmentContextMenu = (e, seg) => {
    // Native context menu kept open via shadcn DropdownMenu trigger; no-op here.
  };

  // Keyboard shortcuts: I → Interviewer, C → Candidate
  const ensureSpeaker = useCallback(async (preferredName) => {
    const existing = speakers.find((s) => s.display_name.toLowerCase() === preferredName.toLowerCase());
    if (existing) return existing.speaker_id;
    const { data } = await api.post(`/transcripts/${transcript.transcript_id}/speakers`, { display_name: preferredName });
    onUpdate(data);
    return data.speakers.find((s) => s.display_name === preferredName)?.speaker_id;
  }, [speakers, transcript.transcript_id, onUpdate]);

  const [focusedIdx, setFocusedIdx] = useState(null);
  useEffect(() => {
    const onKey = async (e) => {
      if (focusedIdx === null) return;
      // Ignore when typing in inputs
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      const k = e.key.toLowerCase();
      if (k !== "i" && k !== "c") return;
      e.preventDefault();
      const name = k === "i" ? "Interviewer" : "Candidate";
      const speakerId = await ensureSpeaker(name);
      if (speakerId) {
        await reassign([{ idx: focusedIdx, speaker_id: speakerId }]);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [focusedIdx, ensureSpeaker]);

  const handleSegmentClick = (seg, e) => {
    if (e.shiftKey && selectionStart !== null) {
      const [lo, hi] = selectionStart < seg.idx ? [selectionStart, seg.idx] : [seg.idx, selectionStart];
      setPendingRanges([{ lo, hi }]);
    } else {
      setSelectionStart(seg.idx);
      setPendingRanges([]);
      setFocusedIdx(seg.idx);
    }
  };

  const bulkAssign = async (speakerId) => {
    let assignments = [];
    if (pendingRanges.length) {
      const { lo, hi } = pendingRanges[0];
      for (let i = lo; i <= hi; i++) {
        assignments.push({ idx: i, speaker_id: speakerId });
      }
    } else if (selectionStart !== null) {
      assignments = [{ idx: selectionStart, speaker_id: speakerId }];
    }
    if (assignments.length) {
      await reassign(assignments);
      setPendingRanges([]);
    }
  };

  const renameAllInstances = async (currentSpeakerId, newName) => {
    // Two options:
    //   (a) rename the speaker (one row change, propagates everywhere)
    //   (b) reassign all matching segments to a different speaker
    // The right-click action says "rename all instances of Speaker X → name"
    // — semantically that's (a): rename the speaker entity.
    await renameSpeaker(currentSpeakerId, newName);
  };

  return (
    <div className="hp-card" data-testid="transcript-view">
      <div className="flex items-center justify-between border-b border-ink-200 px-5 py-3">
        <div className="flex items-center gap-2">
          <h2 className="font-display text-lg font-medium text-ink-900">Transcript</h2>
          <span className="hp-kbd">I</span>
          <span className="text-[11px] text-ink-400">= Interviewer</span>
          <span className="hp-kbd">C</span>
          <span className="text-[11px] text-ink-400">= Candidate</span>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="hp-btn-ghost" data-testid="speakers-menu">
              Speakers <ChevronDown className="h-3 w-3" strokeWidth={STROKE} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-ink-500">
              Speakers in this transcript
            </DropdownMenuLabel>
            {speakers.map((s) => (
              <SpeakerMenuItem
                key={s.speaker_id}
                speaker={s}
                onRename={(name) => renameAllInstances(s.speaker_id, name)}
                onSetRole={async (role) => {
                  const { data } = await api.patch(`/transcripts/${transcript.transcript_id}/speakers/${s.speaker_id}/role`, { role });
                  onUpdate(data);
                }}
              />
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={async () => {
                const lock = transcript.role_classification?.manually_overridden;
                if (lock && !window.confirm("This transcript has manual role overrides. Re-running AI classification will overwrite them. Proceed?")) return;
                try {
                  const { data } = await api.post(
                    `/transcripts/${transcript.transcript_id}/classify-roles${lock ? "?force=true" : ""}`
                  );
                  onUpdate(data);
                  toast.success(lock ? "Roles re-classified (force)" : "Roles re-classified");
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Re-classification failed");
                }
              }}
              data-testid="reclassify-roles-btn"
            >
              <Sparkles className="h-3.5 w-3.5 text-emerald-800" strokeWidth={STROKE} />
              {transcript.role_classification?.manually_overridden
                ? "Re-run AI classification (force)"
                : "Re-run AI classification"}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {creatingSpeaker ? (
              <div className="p-2 flex items-center gap-2">
                <input
                  className="hp-input flex-1"
                  placeholder="Name…"
                  value={newSpeakerName}
                  onChange={(e) => setNewSpeakerName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addSpeaker()}
                  data-testid="new-speaker-input"
                />
                <button className="hp-btn-primary" onClick={addSpeaker} data-testid="new-speaker-submit">Add</button>
              </div>
            ) : (
              <DropdownMenuItem onClick={() => setCreatingSpeaker(true)} data-testid="add-speaker-btn">
                + Add speaker
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="max-h-[60vh] overflow-y-auto px-2 py-2">
        {transcript.segments.length === 0 && (
          <div className="px-5 py-8 text-center text-ink-400 text-[13px]">No segments.</div>
        )}
        {transcript.segments.map((seg) => {
          const inRange = pendingRanges.some(({ lo, hi }) => seg.idx >= lo && seg.idx <= hi);
          const isFocused = focusedIdx === seg.idx;
          const speaker = speakerById[seg.speaker_id] || { display_name: "Unknown" };
          const rowBg = isFocused ? "bg-emerald-50" : inRange ? "bg-emerald-50/60" : "hover:bg-ink-50";
          return (
            <div
              key={seg.idx}
              className={`group flex items-start gap-3 rounded-md px-3 py-2.5 cursor-pointer transition ${rowBg}`}
              data-testid={`segment-${seg.idx}`}
              onClick={(e) => handleSegmentClick(seg, e)}
            >
              {/* Timestamp — mono, fixed-width, aligned with first line of text */}
              <span
                className="font-mono text-[11px] text-ink-400 tabular-nums w-12 pt-0.5 flex-shrink-0"
                data-testid={`segment-timestamp-${seg.idx}`}
              >
                {fmtTime(seg.start)}
              </span>
              {/* Speaker pill + role badge — sits on the SAME row as timestamp and text */}
              <div className="flex-shrink-0 pt-0.5">
                <SpeakerInline
                  speakerId={seg.speaker_id}
                  speakers={speakers}
                  speakerName={speaker.display_name}
                  speakerRole={effectiveRole(speaker)}
                  onAssign={async (newSpeakerId) => {
                    await reassign([{ idx: seg.idx, speaker_id: newSpeakerId }]);
                  }}
                  onRenameSpeaker={(name) => renameAllInstances(seg.speaker_id, name)}
                  onSetRole={async (role) => {
                    const { data } = await api.patch(`/transcripts/${transcript.transcript_id}/speakers/${seg.speaker_id}/role`, { role });
                    onUpdate(data);
                  }}
                />
              </div>
              {/* Text — dominant column, wraps within its own width */}
              <p
                className="flex-1 text-[14px] leading-[1.55] text-ink-800 break-words"
                data-testid={`segment-text-${seg.idx}`}
              >
                {seg.text}
              </p>
            </div>
          );
        })}
        {pendingRanges.length > 0 && (
          <div className="sticky bottom-0 mt-3 rounded-md border border-emerald-800/30 bg-emerald-50 p-3 flex items-center justify-between" data-testid="bulk-assign-bar">
            <span className="text-[12px] text-emerald-900">
              {pendingRanges[0].hi - pendingRanges[0].lo + 1} segments selected. Assign to…
            </span>
            <div className="flex gap-2">
              {speakers.map((s) => (
                <button
                  key={s.speaker_id}
                  onClick={() => bulkAssign(s.speaker_id)}
                  className="rounded-md border border-emerald-800/30 bg-white px-2 py-1 text-[12px] font-medium text-emerald-900 hover:bg-emerald-100"
                  data-testid={`bulk-assign-${s.speaker_id}`}
                >
                  {s.display_name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SpeakerInline({ speakerId, speakers, speakerName, speakerRole, onAssign, onRenameSpeaker, onSetRole }) {
  const [renaming, setRenaming] = useState(false);
  const [val, setVal] = useState(speakerName);
  if (renaming) {
    return (
      <input
        autoFocus
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={() => { setRenaming(false); if (val && val !== speakerName) onRenameSpeaker(val); }}
        onKeyDown={(e) => {
          if (e.key === "Enter") { setRenaming(false); if (val && val !== speakerName) onRenameSpeaker(val); }
          if (e.key === "Escape") { setRenaming(false); setVal(speakerName); }
        }}
        className="mt-1 w-full rounded border border-ink-200 px-1 py-0.5 text-[11px]"
        data-testid={`speaker-rename-input-${speakerId}`}
      />
    );
  }
  const roleKey = speakerRole || "unknown";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          className="inline-flex flex-row items-center gap-1.5"
          data-testid={`segment-speaker-${speakerId}`}
        >
          {/* Speaker pill — compact single-line */}
          <span className="inline-flex items-center gap-0.5 rounded border border-ink-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-emerald-900 hover:border-emerald-700/40 hover:text-ink-900 whitespace-nowrap">
            {speakerName}
            <ChevronDown className="h-3 w-3" strokeWidth={STROKE} />
          </span>
          {/* Role badge — sits to the right of speaker pill, NOT stacked below */}
          <span
            className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap ${ROLE_BADGE_CLASS[roleKey]}`}
            data-testid={`segment-role-badge-${speakerId}`}
          >
            {ROLE_LABELS[roleKey]}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-60" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-ink-500">
          Assign segment to
        </DropdownMenuLabel>
        {speakers.map((s) => (
          <DropdownMenuItem
            key={s.speaker_id}
            onClick={() => onAssign(s.speaker_id)}
            data-testid={`assign-to-${s.speaker_id}`}
          >
            {s.display_name}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-ink-500">
          Set role for {speakerName}
        </DropdownMenuLabel>
        {["interviewer", "candidate", "panelist", "unknown"].map((r) => (
          <DropdownMenuItem
            key={r}
            onClick={() => onSetRole(r)}
            data-testid={`set-role-${speakerId}-${r}`}
          >
            <span className={`mr-1 inline-block h-1.5 w-1.5 rounded-full ${ROLE_BADGE_CLASS[r].split(' ')[0]}`} />
            {ROLE_LABELS[r]}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => setRenaming(true)} data-testid={`rename-all-${speakerId}`}>
          <Pencil className="h-3.5 w-3.5" strokeWidth={STROKE} />
          Rename all &quot;{speakerName}&quot; instances…
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SpeakerMenuItem({ speaker, onRename, onSetRole }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(speaker.display_name);
  const role = speaker.role || speaker.suggested_role || "unknown";
  const isOverride = !!speaker.role;
  if (editing) {
    return (
      <div className="p-2 flex items-center gap-2">
        <input
          autoFocus
          className="hp-input flex-1"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (onRename(val), setEditing(false))}
          data-testid={`speaker-rename-input-${speaker.speaker_id}`}
        />
        <button className="hp-btn-primary" onClick={() => { onRename(val); setEditing(false); }} data-testid={`speaker-rename-save-${speaker.speaker_id}`}>Save</button>
      </div>
    );
  }
  return (
    <div className="px-2 py-1.5 flex items-center justify-between gap-2" data-testid={`speaker-menu-${speaker.speaker_id}`}>
      <button
        onClick={() => setEditing(true)}
        className="flex items-center gap-1.5 text-left text-[13px] text-ink-800 hover:text-ink-900"
        data-testid={`speaker-rename-trigger-${speaker.speaker_id}`}
      >
        <Pencil className="h-3 w-3 text-ink-400" strokeWidth={STROKE} />
        {speaker.display_name}
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={`inline-flex items-center gap-1 rounded border px-1.5 py-px text-[10px] font-medium ${ROLE_BADGE_CLASS[role]}`}
            data-testid={`speaker-role-badge-${speaker.speaker_id}`}
            title={isOverride ? "Manual override" : "AI suggestion"}
          >
            {ROLE_LABELS[role]}{isOverride ? "·set" : ""}
            <ChevronDown className="h-2.5 w-2.5" strokeWidth={STROKE} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-ink-500">
            Override role
          </DropdownMenuLabel>
          {["interviewer", "candidate", "panelist", "unknown"].map((r) => (
            <DropdownMenuItem
              key={r}
              onClick={() => onSetRole(r)}
              data-testid={`speaker-set-role-${speaker.speaker_id}-${r}`}
            >
              <span className={`mr-1 inline-block h-1.5 w-1.5 rounded-full ${ROLE_BADGE_CLASS[r].split(' ')[0]}`} />
              {ROLE_LABELS[r]}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// ---------- Summary view ----------

function SummaryView({ summary, onRegenerate, canRegenerate }) {
  const c = summary.content || {};
  return (
    <div className="hp-card" data-testid="summary-view">
      <div className="flex items-center justify-between border-b border-ink-200 px-5 py-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-emerald-800" strokeWidth={STROKE} />
          <h2 className="font-display text-lg font-medium text-ink-900">Interview summary</h2>
        </div>
        {canRegenerate && (
          <button onClick={onRegenerate} className="hp-btn-ghost" data-testid="summary-regenerate">
            <RefreshCw className="h-3.5 w-3.5" strokeWidth={STROKE} /> Regenerate
          </button>
        )}
      </div>
      <div className="space-y-5 p-5 text-[14px] leading-relaxed text-ink-800">
        {c.candidate_overview && (
          <Section label="Candidate"><p>{c.candidate_overview}</p></Section>
        )}
        {c.role_context && (
          <Section label="Role"><p>{c.role_context}</p></Section>
        )}
        {c.key_topics?.length > 0 && (
          <Section label="Key topics">
            <ul className="space-y-1.5 list-disc list-inside">
              {c.key_topics.map((t, i) => <li key={`${i}-${t.slice(0, 40)}`}>{t}</li>)}
            </ul>
          </Section>
        )}
        {c.technical_responses?.length > 0 && (
          <Section label="Technical responses">
            <ul className="space-y-2">
              {c.technical_responses.map((r, i) => (
                <li key={`${i}-${r.competency || r.timestamp || ""}`}>
                  <div className="font-medium text-ink-900">{r.competency}</div>
                  <div className="text-ink-700">{r.response} <span className="font-mono text-[11px] text-ink-400">{r.timestamp}</span></div>
                </li>
              ))}
            </ul>
          </Section>
        )}
        {c.behavioral_observations?.length > 0 && (
          <Section label="Behavioral observations (verbal content only)">
            <ul className="space-y-1.5 list-disc list-inside">
              {c.behavioral_observations.map((b, i) => <li key={`${i}-${b.slice(0, 40)}`}>{b}</li>)}
            </ul>
          </Section>
        )}
        {c.notable_quotes?.length > 0 && (
          <Section label="Notable quotes">
            <ul className="space-y-3">
              {c.notable_quotes.map((q, i) => (
                <li key={`${i}-${q.timestamp || q.quote?.slice(0, 30)}`} className="rounded-md border-l-2 border-emerald-800/40 bg-ink-50 px-3 py-2">
                  <p className="font-display text-[14px] italic">&ldquo;{q.quote}&rdquo;</p>
                  <span className="mt-1 inline-block font-mono text-[11px] text-ink-400">{q.timestamp}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}
        {c.open_questions?.length > 0 && (
          <Section label="Open questions for next round">
            <ul className="space-y-1.5 list-disc list-inside">
              {c.open_questions.map((q, i) => <li key={`${i}-${q.slice(0, 40)}`}>{q}</li>)}
            </ul>
          </Section>
        )}
        {c.suggested_next_steps?.length > 0 && (
          <Section label="Suggested next steps">
            <ul className="space-y-1.5 list-disc list-inside">
              {c.suggested_next_steps.map((s, i) => <li key={`${i}-${s.slice(0, 40)}`}>{s}</li>)}
            </ul>
          </Section>
        )}
        <div className="pt-3 border-t border-ink-100">
          <ComplianceDisclaimer />
        </div>
      </div>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-ink-500 mb-1.5">{label}</div>
      {children}
    </div>
  );
}
